import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { useBackdropContext } from '../../../../../../../context/backdrop';
import { OptimizerModeInternal } from '../../../../../../../data-access/gql-types/graphql';
import { OptimizerModeInternalRequest } from '../../../../../types/graphql-rest-converter';
import { toastSuccess } from '../../../../../../../utils/toast';
import { useOptimizerSetConfig, useOptimizerSetMode } from '../../../../../api/optimizer/optimizer.hooks';
import { ChannelType } from '../../../../../types';
import { useOptimizerConfigurationContext } from '../../context/configuration-context';
import { FullConsumptionFormBody } from '../types';
import {
  prepareHoursRangesToSend,
  prepareOperatorAndTariffForRequest,
  prepareRelaysToSend,
  prepareWorkingHoursToSend,
} from '../utils';
import { useOptimizerErrors } from './use-optimizer-errors';

export const useOptimizerFullConsumption = (channel: ChannelType) => {
  const { t } = useTranslation('channel-details');
  const { mutate: setMode } = useOptimizerSetMode();
  const { mutate: setConfig } = useOptimizerSetConfig();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { handleOptimizerErrors } = useOptimizerErrors();
  const { clearErrors } = useOptimizerConfigurationContext();

  const submitConfiguration = (
    mode: OptimizerModeInternal,
    formValues: FullConsumptionFormBody,
    closeWarningDialog?: () => void,
  ) => {
    const {
      tempMinEnabled,
      tempMax,
      tempMin,
      tempSelfConsumption,
      heaterPowerLimit,
      voltageThreshold1,
      voltageThreshold2,
      voltageThreshold3,
      relayConfig1,
      relayConfig2,
      relayConfig3,
      relayConfig4,
      from,
      to,
      wholeWeekendCheaper,
      wholeHolidaysCheaper,
      ignoreSummerTimeChanges,
      hoursRanges,
      operator,
      tariffName,
    } = formValues;
    clearErrors();

    turnOnBackdrop();
    setMode(
      {
        deviceId: channel.deviceId,
        channelId: channel.id,
        mode: OptimizerModeInternalRequest[mode],
      },
      {
        onSuccess: ({ data }) => {
          if (data) {
            turnOnBackdrop();

            setConfig(
              {
                deviceId: channel.deviceId,
                channelId: channel.id,
                fullSelfConsumption: {
                  tempMinEnabled,
                  tempMax,
                  tempMin,
                  tempSelfConsumption,
                  heaterPowerLimit,
                  voltageThreshold: [
                    { index: 1, value: voltageThreshold1 },
                    { index: 2, value: voltageThreshold2 },
                    { index: 3, value: voltageThreshold3 },
                  ],
                  relaysConfig: prepareRelaysToSend({ relayConfig1, relayConfig2, relayConfig3, relayConfig4 }),
                  workingHours: prepareWorkingHoursToSend(from, to),
                  ...(tempMinEnabled
                    ? {
                        tariff: {
                          wholeWeekendCheaper,
                          wholeHolidaysCheaper,
                          ignoreSummerTimeChanges,
                          hoursRanges: prepareHoursRangesToSend(hoursRanges),
                          kind: prepareOperatorAndTariffForRequest(operator, tariffName),
                        },
                      }
                    : {}),
                },
              },
              {
                onSuccess: () => {
                  turnOffBackdrop();
                  closeWarningDialog?.();
                  toastSuccess({ content: t('optimizer.configuration.configurationSaved') });
                },
                onError: (error: AxiosError) => {
                  closeWarningDialog?.();
                  handleOptimizerErrors(error);
                },
              },
            );
          } else {
            turnOffBackdrop();
          }
        },
      },
    );
  };

  return {
    submitConfiguration,
  };
};
